import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';

import { SortableElement, SortableHandle } from 'react-sortable-hoc';
import {
  ColorPicker,
  ThemedInput,
  Icons,
  colors,
  SingleSelectDropdown,
  ConfirmModal,
  Tooltip,
  ToggleSwitch,
} from '@united-talent-agency/components';
import { HEADER } from '../../../support/cypressTags';
import { useFlags } from 'launchdarkly-react-client-sdk';

const { GripIcon } = Icons;
const { XCloseIcon } = Icons;

const DragHandle = SortableHandle(({ cypressTag, onMouseDown, onMouseUp }) => (
  <div
    data-cy={cypressTag}
    onMouseDown={onMouseDown}
    onMouseUp={onMouseUp}
    onMouseLeave={onMouseUp}
    style={{ cursor: 'grab', alignItems: 'center', display: 'flex', marginRight: 8 }}
  >
    <GripIcon inline width={20} />
  </div>
));

const StatusRow = SortableElement(
  ({
    status = {},
    totalCounts,
    updateColor,
    updateName,
    updateAbbr,
    updateType,
    updateCount,
    updateInfiniteLoading,
    errorState,
    removeStatus,
  }) => {
    const { infiniteLoadingByStatus } = useFlags();

    // States
    const [dragging, setDragging] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const anyFieldFilled = status.name || status.color || status.type || status.status;
    const statusRowSelectItems = [
      { value: 'Initiate', label: 'Initiate' },
      { value: 'On Going', label: 'On Going' },
      { value: 'Close', label: 'Close' },
    ];

    useEffect(() => {
      if (status?._id) {
        updateCount(totalCounts[status.status]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showConfirmModal = () => {
      if (!status?.hasCallRecord) {
        setShowDeleteModal(true);
      }
    };

    const doRemoveStatus = () => {
      removeStatus();
      setShowDeleteModal(false);
    };

    const handleMouseDown = () => {
      document.body.classList.add('no-select');
      setDragging(true);
    };

    const handleMouseUp = () => {
      document.body.classList.remove('no-select');
      setDragging(false);
    };

    return (
      <>
        <StatusRowListItem dragging={dragging}>
          <DragHandle
            cypressTag={HEADER.STATUS_DRAG_HANDLE}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
          />
          <div
            data-cy={HEADER.SETTINGS.MODAL_STATUS_TEXT_BOX}
            style={{ width: 134, marginRight: 4 }}
          >
            <ThemedInput
              required={anyFieldFilled || errorState}
              title="Status"
              value={status.name}
              maxLength={25}
              onChange={(e) => {
                updateName(e.target.value);
              }}
              error={(errorState && !status.name) || status?.name?.length >= 25}
            />
          </div>
          <div data-cy={HEADER.STATUS_ABBR} style={{ width: 52, marginRight: 4 }}>
            <ThemedInput
              required={anyFieldFilled || errorState}
              title="Initial"
              value={status.status}
              maxLength={2}
              onChange={(e) => {
                updateAbbr(e.target.value);
              }}
              error={errorState && !status.status}
            />
          </div>
          <ColorPicker
            required={anyFieldFilled || errorState}
            value={status.color || ''}
            onChange={(color) => {
              updateColor(color.hex);
            }}
            error={errorState && !status.color}
            top={'20%'}
            left={'25%'}
          />
          <div style={{ width: 134, margin: '0 4px' }}>
            <SingleSelectDropdown
              required={anyFieldFilled || errorState}
              title={'Type'}
              value={status.type && { value: status.type, label: status.type }}
              options={statusRowSelectItems}
              onChange={({ value }) => updateType(value)}
              error={errorState && !status.type}
            />
          </div>
          {infiniteLoadingByStatus && (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '0px 5px',
                maxWidth: 100,
              }}
            >
              <Tooltip
                text="If enabled, additional records for this status load automatically while scrolling."
                place="bottom"
                width="350px"
              >
                <span
                  style={{
                    fontSize: '12px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    marginBottom: 'auto',
                  }}
                >
                  Auto paginate
                </span>
              </Tooltip>
              <ToggleSwitch
                value={status?.infiniteLoading}
                onChange={() => updateInfiniteLoading(!status?.infiniteLoading)}
              />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {status?.hasCallRecord ? (
              <Tooltip
                text="Reassign all calls with this status in order to delete it (includes hidden future calls)"
                width="250px"
              >
                <CloseButton type="button" tabIndex={0} onClick={showConfirmModal}>
                  <XCloseIcon
                    style={{ cursor: 'pointer' }}
                    color={colors.disabledGrey}
                    inline
                    width={18}
                    height={18}
                  />
                </CloseButton>
              </Tooltip>
            ) : (
              <CloseButton type="button" tabIndex={0} onClick={showConfirmModal}>
                <XCloseIcon
                  style={{ cursor: 'pointer' }}
                  color={colors.red}
                  inline
                  width={18}
                  height={18}
                />
              </CloseButton>
            )}
          </div>
        </StatusRowListItem>
        {status?.name?.length >= 25 && (
          <ErrorMessage>25 char limit reached for Status Name</ErrorMessage>
        )}
        {errorState && <ErrorMessage>All fields are required</ErrorMessage>}

        <ConfirmModal
          title="Delete Confirmation"
          message="Are you sure you want to permanently delete this status?"
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          isOpen={showDeleteModal}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={doRemoveStatus}
        />
      </>
    );
  }
);

const StatusRowListItem = styled.li(({ dragging }) => ({
  display: 'flex',
  flexDirection: 'row',
  flex: '0 1 100%',
  backgroundColor: dragging && colors.focusGrey,
  padding: '0 15px',
}));

const ErrorMessage = styled.p({
  fontSize: 12,
  margin: '-3px 46px 9px',
  color: 'red',
});

const CloseButton = styled.button({
  margin: 0,
  padding: 0,
  backgroundColor: 'transparent',
  outline: 'none',
  border: '1px solid transparent',
  ':focus': {
    border: '1px solid rgb(33, 135, 185)',
  },
});

export default StatusRow;
